import { useNavigate } from 'react-router-dom';
import { ErrorSource } from '../../../core/constant/model/api/api';
import { createApiError } from '../../../core/constant/utils/error.helper';
import { getDataFromResponse } from '../../../core/constant/utils/react-query-utils/getDataFromResponse';
import { createApiMutation } from '../../../core/hooks/useMutationFactory';
import {
  createPaymentIntent,
  createPaymentIntentWithPaymentId,
  createStripeCustomer,
  updatePaymentIntent,
  validatePromoCode,
  redeemGiftCard,
} from '../api/subscription.api';
import {
  CreatePaymentIntentResponse,
  CreateStripeCustomerResponse,
  ValidatePromoCodeResponse,
} from '../types/subscription';
import toastService from '../../../services/toastService';

export const useCreateStripeCustomerMutation = () => {
  return createApiMutation<CreateStripeCustomerResponse, void>({
    mutationFn: async (_, signal) => {
      const response = await createStripeCustomer(signal);
      const responseData = getDataFromResponse(response);

      if (responseData.statusCode && responseData.statusCode !== 200) {
        throw createApiError(
          responseData.message || 'Failed to create Stripe customer',
          responseData
        );
      }

      return responseData;
    },
    errorSource: ErrorSource.CREATE_STRIPE_CUSTOMER,
    loadingActions: {
      start: (dispatch) => {},
      end: (dispatch) => {},
    },
    onSuccessHandler: (responseData, variables, context, dispatch) => {},
    module: 'Subscription',
  });
};

export const useCreatePaymentIntentMutation = () => {
  const navigate = useNavigate();

  return createApiMutation<
    CreatePaymentIntentResponse,
    {
      priceId: string;
      paymentMethodId: string;
      isAddOn: boolean;
      isRefCodeAvailable?: boolean | undefined;
      promoCode?: string;
      referralDiscountPercent?: number;
      referralCode?: string;
      trialPeriodDays?: number;
    }
  >({
    mutationFn: async (
      {
        priceId,
        paymentMethodId,
        isAddOn,
        isRefCodeAvailable,
        promoCode,
        referralDiscountPercent,
        referralCode,
        trialPeriodDays,
      },
      signal
    ) => {
      const response = await createPaymentIntent(
        priceId,
        paymentMethodId,
        isAddOn,
        isRefCodeAvailable,
        promoCode,
        referralDiscountPercent,
        referralCode,
        trialPeriodDays,
        signal
      );
      const responseData = getDataFromResponse(response);

      if (
        responseData.statusCode &&
        responseData.statusCode !== 201 &&
        responseData.statusCode !== 200
      ) {
        throw createApiError(
          responseData.message || 'Failed to create payment intent',
          responseData
        );
      }

      // Check if paymentRequired is false
      if (responseData.data?.paymentRequired === false && responseData.data?.subscriptionId) {
        // Call the endpoint again with subscriptionId as paymentId parameter and the full payload
        const secondResponse = await createPaymentIntentWithPaymentId(
          priceId,
          paymentMethodId,
          isAddOn,
          responseData.data.subscriptionId,
          isRefCodeAvailable,
          promoCode,
          referralDiscountPercent,
          referralCode,
          trialPeriodDays,
          signal
        );
        const secondResponseData = getDataFromResponse(secondResponse);

        if (secondResponseData.statusCode && secondResponseData.statusCode !== 200) {
          if (secondResponseData.message.includes('No such payment_intent')) {
            toastService.success('Great news! Your payment was processed successfully.');
            navigate('/addition-information', { replace: true });
          }
          throw createApiError(
            secondResponseData.message || 'Failed to create payment intent with paymentId',
            secondResponseData
          );
        }
        toastService.success('Great news! Your payment was processed successfully.');
        navigate('/addition-information', { replace: true });

        return secondResponseData;
      }

      return responseData;
    },
    errorSource: ErrorSource.CREATE_PAYMENT_INTENT,
    loadingActions: {
      start: (dispatch) => {},
      end: (dispatch) => {},
    },
    onSuccessHandler: (responseData, variables, context, dispatch) => {},
    module: 'Subscription',
  });
};

export const useUpdatePaymentIntentMutation = () => {
  return createApiMutation<any, string>({
    mutationFn: async (clientSecret, signal) => {
      const response = await updatePaymentIntent(clientSecret, null, signal);
      const responseData = getDataFromResponse(response);

      if (responseData.statusCode && responseData.statusCode !== 200) {
        throw createApiError(
          responseData.message || 'Failed to update payment intent',
          responseData
        );
      }

      return responseData;
    },
    errorSource: ErrorSource.UPDATE_PAYMENT_INTENT,
    loadingActions: {
      start: (dispatch) => {},
      end: (dispatch) => {},
    },
    onSuccessHandler: (responseData, variables, context, dispatch) => {},
    module: 'Subscription',
  });
};

export const useValidatePromoCodeMutation = () => {
  return createApiMutation<ValidatePromoCodeResponse, string>({
    mutationFn: async (code, signal) => {
      const response = await validatePromoCode(code, signal);
      const responseData = getDataFromResponse(response);

      if (responseData.statusCode && responseData.statusCode !== 200) {
        throw createApiError(responseData.message || 'Failed to validate promo code', responseData);
      }

      return responseData;
    },
    errorSource: ErrorSource.VALIDATE_PROMO_CODE,
    loadingActions: {
      start: (dispatch) => {},
      end: (dispatch) => {},
    },
    onSuccessHandler: (responseData, variables, context, dispatch) => {},
    module: 'Subscription',
  });
};

export const useRedeemGiftCardMutation = () => {
  return createApiMutation<
    any,
    {
      code: string;
      amount: number;
      reason: string;
      metadata?: Record<string, any>;
      accessToken?: string;
    }
  >({
    mutationFn: async ({ code, amount, reason, metadata, accessToken }, signal) => {
      const response = await redeemGiftCard(code, amount, reason, metadata, accessToken, signal);
      const responseData = getDataFromResponse(response);

      if (responseData.statusCode && responseData.statusCode !== 200) {
        throw createApiError(responseData.message || 'Failed to redeem gift card', responseData);
      }

      return responseData;
    },
    errorSource: ErrorSource.CREATE_PAYMENT_INTENT,
    loadingActions: {
      start: (dispatch) => {},
      end: (dispatch) => {},
    },
    onSuccessHandler: (responseData, variables, context, dispatch) => {},
    module: 'Subscription',
  });
};
